/**
 * Constantes pour le système d'abonnements CashLive
 * Plans: FREEMIUM, PRO et LAUNCH_OFFER
 */

/**
 * Types de plans disponibles
 */
export type PlanType = 'FREEMIUM' | 'PRO' | 'LAUNCH_OFFER';

/**
 * Date de fin de l'offre de lancement (30 mai 2026)
 */
export const LAUNCH_OFFER_END_DATE = new Date('2026-05-30T23:59:59');

/** Commission CashLive après la fin de l'offre de lancement (plan LAUNCH_OFFER) */
export const LAUNCH_OFFER_POST_COMMISSION_RATE = 2.5;

/**
 * Configuration des plans
 */
export const SUBSCRIPTION_PLANS = {
    FREEMIUM: {
        id: 'FREEMIUM',
        name: 'Freemium',
        price: 0, // euros/mois
        commissionRate: 2.8, // %
        welcomeBonus: 0, // Bonus supprimé
        features: {
            iaAssistant: false, // Non inclus, crédits à acheter
            savChat: false,
            savHistory: false,
        },
        description: 'Démarrez gratuitement',
    },
    PRO: {
        id: 'PRO',
        name: 'Pro',
        price: 59, // euros/mois
        priceInCents: 5900, // pour Stripe
        commissionRate: 2.0, // %
        welcomeBonus: 0, // Bonus supprimé
        features: {
            iaAssistant: true, // Illimité (fair use)
            savChat: true,
            savHistory: true,
        },
        description: 'Commission réduite + IA Assistant illimité + SAV complet',
        stripeProductId: '', // À configurer dans Stripe
        stripePriceId: '', // À configurer dans Stripe
    },
    LAUNCH_OFFER: {
        id: 'LAUNCH_OFFER',
        name: 'Offre de Lancement',
        price: 0, // euros/mois
        commissionRate: LAUNCH_OFFER_POST_COMMISSION_RATE, // % après le 30 mai
        welcomeBonus: 0,
        features: {
            iaAssistant: false,
            savChat: false,
            savHistory: false,
        },
        description: 'Offre spéciale : 0% de commission jusqu\'au 30 mai 2026',
    },
} as const;

/**
 * Bonus de bienvenue supprimé (0€)
 */
export const WELCOME_BONUS_AMOUNT = 0;

/**
 * Commission CashLive à 0%
 */
export const BONUS_COMMISSION_RATE = 0;

/**
 * Valeurs par défaut
 */
export const DEFAULT_PLAN: PlanType = 'FREEMIUM';

/**
 * Calcule la commission CashLive
 * 
 * @param planType - Type de plan (FREEMIUM, PRO ou LAUNCH_OFFER)
 * @param currentRevenue - (inutilisé, legacy)
 * @param transactionAmount - (inutilisé, legacy)
 * @returns Taux de commission en pourcentage et infos bonus
 */
export function calculateCashLiveCommission(
    planType: PlanType,
    currentRevenue: number,
    transactionAmount: number
): { commissionRate: number; bonusRemaining: number; bonusApplied: boolean } {
    const plan = SUBSCRIPTION_PLANS[planType];

    // Logique spéciale pour l'offre de lancement
    if (planType === 'LAUNCH_OFFER') {
        const now = new Date();
        const isOfferActive = now <= LAUNCH_OFFER_END_DATE;

        if (isOfferActive) {
            return {
                commissionRate: 0,
                bonusRemaining: 0,
                bonusApplied: true, // Considéré comme "bonus"
            };
        } else {
            return {
                commissionRate: LAUNCH_OFFER_POST_COMMISSION_RATE,
                bonusRemaining: 0,
                bonusApplied: false,
            };
        }
    }

    return {
        commissionRate: plan.commissionRate,
        bonusRemaining: 0,
        bonusApplied: false,
    };
}

/**
 * Calcule le montant du bonus restant (toujours 0 pour standard, ou infini pour launch offer activ)
 */
export function getBonusRemaining(currentRevenue: number, bonusLimit: number = WELCOME_BONUS_AMOUNT): number {
    return 0;
}

/**
 * Vérifie si le bonus est épuisé (toujours vrai sauf config spécifique)
 */
export function isBonusExhausted(currentRevenue: number, bonusLimit: number = WELCOME_BONUS_AMOUNT): boolean {
    return true;
}

/**
 * Formate un montant en centimes pour l'affichage
 */
export function formatCentsToEuros(cents: number): string {
    return (cents / 100).toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
    });
}
